import React from 'react';
import { safeHref } from '@/utils/url-utils';
export default {
  'cart': {
    'change-currency': 'Change currency',
    'and-then': 'And then',
    'plus-tax': '+ tax',
    'for': 'For',
    'edit': {
      'label': 'Edit quantities',
      'submit': 'Apply'
    },
    'empty': 'You haven’t added anything to your order yet.',
    'free': 'free',
    'promo-code': {
      'add': 'Add gift card or promo code',
      'error': {
        'invalid-for-subscription': 'Coupon code is not valid for this order.'
      },
      'label': 'Gift card or promo code',
      'submit': 'Apply'
    },
    'subtotal-line': {
      'tax-label': 'Estimated tax',
      'subtotal': 'Subtotal',
      'tax': 'Tax',
      'gift-card': 'Gift Card'
    },
    'starting-price': 'Starts at',
    'title': 'Order Summary',
    'setup-fee': 'setup fee',
    'free-trial': 'Free trial',
    'total-line': {
      'label': 'Total'
    },
    'order-total-line': {
      'label': 'Order Total'
    }
  },
  'address': {
    'country': 'Country',
    'address2': 'Apartment, suite, etc.',
    'tax-identifier': 'Tax Identifier',
    'tax-identifier-type': 'Tax Identifier type',
    'first-name': 'First Name',
    'last-name': 'Last Name',
    'company-name': 'Company Name',
    'address1': 'Street Address',
    'city': 'City',
    'state': 'State',
    'postal-code': 'Zip/Postal',
    'phone-number': 'Phone Number',
    'vat-number': 'VAT Number/Tax ID'
  },
  'payment-method': {
    'credit-card': {
      'label': 'Card details',
      'expiry': 'MM/YY',
      'name': 'Credit Card',
      'number': 'Card Number',
      'cvv': 'CVV',
      'brand': {
        'visa': 'Visa',
        'master': 'MasterCard',
        'cartes_bancaires': 'Cartes Bancaires'
      }
    },
    'ach': {
      'name': 'Bank Account',
      'routing-number': 'Routing Number',
      'account-number': 'Account Number',
      'account-number-confirmation': 'Confirm Account Number',
      'account-type': {
        'label': 'Account Type',
        'checking': 'Checking',
        'savings': 'Savings'
      },
      'name-on-account': 'Name on Account'
    },
    'amazon': {
      'name': 'Amazon Pay'
    },
    'apple-pay': {
      'name': 'Apple Pay'
    },
    'boleto': {
      'name': 'Boleto'
    },
    'bacs': {
      'name': 'Bacs',
      'sort-code': 'Sort Code'
    },
    'becs': {
      'name': 'BECS',
      'bsb-code': 'BSB Code'
    },
    'google-pay': {
      'name': 'Google Pay'
    },
    'ideal': {
      'name': 'Ideal'
    },
    'paypal': {
      'name': 'PayPal'
    },
    'qiwi_wallet': {
      'name': 'Qiwi Wallet'
    },
    'sepa': {
      'name': 'SEPA',
      'iban': 'IBAN'
    },
    'sofort': {
      'name': 'Sofort'
    },
    'venmo': {
      'name': 'Venmo'
    }
  },
  'billing-address': 'Billing Address',
  'billing-address-same-as-shipping': 'Same as shipping address',
  'submit': 'Pay now',
  'payment-method-selected': ({ paymentMethod }: { paymentMethod: string }) => <>{paymentMethod} selected</>,
  'express-checkout': 'Express Checkout',
  'change-payment-method': 'Change payment method',
  'day': 'day',
  'days': 'days',
  'week': 'week',
  'weeks': 'weeks',
  'month': 'month',
  'months': 'months',
  'year': 'year',
  'years': 'years',
  'confirmation': {
    'title': 'Thank you for your order',
    'order-num': ({ number }: { number: number }) => <>Order #{number}</>,
    'fulfilled-on': ({ date }: { date: string }) => <>Completed on {date}</>,
    'email-notice': 'You will receive an email confirmation of your order.',
    'payment': 'Payment',
    'return-to': ({ name }: { name: string }) => <>Return to {name}</>,
    'credit-applied': 'Credit Applied'
  },
  'back': 'Back',
  'contact-info': 'Contact Information',
  'email-address': 'Email',
  'payment-info': 'Billing Information',
  'shipping-address': 'Shipping Address',
  'legal': {
    'accept': 'I accept the',
    'privacy-policy': 'Privacy Policy',
    'terms-of-service': 'Terms of Service'
  },
  'recurring': {
    'consent': `I agree to pay the charges above, displayed with a billing frequency, on a recurring basis,
                unless the subscription is cancelled before the end of the current billing period.`,
    'cancel': 'To cancel your subscription, visit your Account Management page.',
    'cancel-with-link': ({ accountManagementUrl }) => (
      <> To cancel your subscription, visit your <a href={safeHref(accountManagementUrl)} target="_blank" rel="noopener noreferrer">Account Management</a> page.</>
    ),
  },
  'pluralize': {
    'days': ({ count }: { count: number }) => count === 1 ? 'day' : 'days',
    'weeks': ({ count }: { count: number }) => count === 1 ? 'week' : 'weeks',
    'months': ({ count }: { count: number }) => count === 1 ? 'month' : 'months',
    'years': ({ count }: { count: number }) => count === 1 ? 'year' : 'years'
  },
  'subscription': {
    'details': {
      'addons': {
        'flat.charge': ({ amount, addOnName }: { amount: string; addOnName: string }) => <>You will be charged {amount}/unit for {addOnName}</>,
        'no-flat.charge': ({ amount, units, unitsLabel, addOnName }: { amount: string; units: number; unitsLabel: string; addOnName: string }) => <>You will be charged {amount} for {units} {unitsLabel} of {addOnName}</>
      },
      'billing.starting': ({ startDate, amount, intervalLengthLabel, taxable = true }: { startDate: string; amount: string; intervalLengthLabel: string; taxable?: boolean }) => <>Starting {startDate} you will be billed {amount}/{intervalLengthLabel}{taxable ? ' + tax' : ''}</>,
      'billing.starting.setupfee': ({ startDate, amount, intervalLengthLabel, setupFee, taxable = true }: { startDate: string; amount: string; intervalLengthLabel: string; setupFee: string; taxable?: boolean }) => <>Starting {startDate} you will be billed {amount}/{intervalLengthLabel}{taxable ? ' + tax' : ''}, along with a one-time fee of {setupFee}</>,
      'cancellation.reminder': 'You\'ll get an email reminder {days} days before billing begins.',
      'trial.start': ({ trialLength, trialInterval }: { trialLength: string; trialInterval: string }) => <>Your free {trialLength} {trialInterval} trial begins today.</>,
      'trial.ends': ({ trialEndsAt }: { trialEndsAt: string }) => <>To avoid being charged, you must cancel before {trialEndsAt}.</>,
      'email.reminder': ({ days }: { days: number }) => <>You’ll get an email reminder {days} days before billing begins</>,
      'no-renewal': ({ billingEndsAt }: { billingEndsAt: string }) => <>Your subscription will end on {billingEndsAt} and will not automatically renew.</>,
      'ramp.after': ({ afterChargeTime, newChargeAmount }: { afterChargeTime: string; newChargeAmount: string }) => <>After {afterChargeTime}, you will be charged {newChargeAmount} + tax</>,
      'renewal': ({ totalCyclesLengthLabel }: { totalCyclesLengthLabel: string }) => <>Your subscription will renew automatically every {totalCyclesLengthLabel} and continue until you cancel.</>,
      'title': 'Subscription Details',
    },
    'agreement.button': 'Agree & Continue',
    'agreement.text': 'By clicking "Agree & Continue", you confirm that you understand and accept these subscription terms. No charges will apply until you finish checkout.',
    'changeOrCancel': 'To change or cancel your subscription ',
    'access-account': 'access your account',
    'contact-support': 'contact support',
  },
  'currency-display': ({ currency }: { currency: string }) => <>{(currency || '').toUpperCase()}</>,
  'support': {
    'array': {
      'wordsConnector': ', ',
      'twoWordsConnector': ' and ',
      'lastWordConnector': ', and '
    }
  },
  'powered-by': ({ recurly }: { recurly: string }) => <>Subscription billing powered by {recurly}</>,
  'errors': {},
  'subscribe': 'Subscribe'
};
